import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, ArrowRight, Loader2, Download } from "lucide-react";
import { api, whatsappLink } from "@/lib/api";

const CATEGORIES = [
  { slug: "carpet", label: "Carpet" },
  { slug: "rug", label: "Rug" },
  { slug: "photo_frame", label: "Photo Frame" },
  { slug: "kids_basket", label: "Kids Basket" },
];

const MATERIALS_BY_CAT = {
  carpet: ["wool", "silk", "silk_blend", "cotton"],
  rug: ["wool", "cotton", "jute", "handloom", "recycled_fabric"],
  photo_frame: ["teak", "oak", "walnut"],
  kids_basket: ["seagrass", "cotton", "bamboo", "jute"],
};

const PALETTES = [
  "warm neutral",
  "deep forest",
  "ivory & sage",
  "terracotta & ochre",
  "midnight & brass",
  "soft rose & bone",
];

const STYLES = [
  "modern minimalist",
  "heritage medallion",
  "abstract botanical",
  "geometric bauhaus",
  "watercolour bloom",
  "childlike sketch",
];

export default function Designer() {
  const fileRef = useRef(null);
  const [category, setCategory] = useState("carpet");
  const [material, setMaterial] = useState("wool");
  const [width, setWidth] = useState(200);
  const [length, setLength] = useState(300);
  const [quantity, setQuantity] = useState(1);
  const [customDesign, setCustomDesign] = useState(true);

  const [prompt, setPrompt] = useState(
    "A quiet meadow at first light — soft ivory ground with faint sage vines."
  );
  const [style, setStyle] = useState("modern minimalist");
  const [palette, setPalette] = useState("warm neutral");

  const [refImage, setRefImage] = useState(null); // base64
  const [refImageName, setRefImageName] = useState("");
  const [generated, setGenerated] = useState(null);

  const [estimate, setEstimate] = useState(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [loadingGen, setLoadingGen] = useState(false);

  const currentMaterials = MATERIALS_BY_CAT[category] || [];

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Please upload an image under 5MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString() || "";
      const base = result.split(",")[1] || "";
      setRefImage(base);
      setRefImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const runEstimate = async () => {
    setLoadingEstimate(true);
    try {
      const { data } = await api.post("/estimate", {
        category,
        material,
        width_cm: Number(width),
        length_cm: Number(length),
        quantity: Number(quantity),
        custom_design: customDesign,
      });
      setEstimate(data);
      toast.success("Estimate ready");
    } catch (e) {
      toast.error("Could not estimate right now");
    } finally {
      setLoadingEstimate(false);
    }
  };

  const runGenerate = async () => {
    setLoadingGen(true);
    setGenerated(null);
    try {
      const { data } = await api.post("/generate-carpet-design", {
        prompt,
        material,
        style,
        palette,
        reference_image_base64: refImage || null,
      });
      setGenerated(data);
      toast.success("Design generated");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Design generation failed. Please try again."
      );
    } finally {
      setLoadingGen(false);
    }
  };

  const enquireOnWhatsApp = () => {
    const parts = [
      `Hello Iryaa Homes, I would like to commission a custom ${CATEGORIES.find(
        (c) => c.slug === category
      )?.label}.`,
      `Material: ${material.replace("_", " ")}`,
      `Size: ${width} × ${length} cm, Qty: ${quantity}`,
      `Style: ${style} · Palette: ${palette}`,
      `Motif: ${prompt}`,
    ];
    if (estimate) {
      parts.push(
        `Indicative estimate: ₹${estimate.price_range_low_inr.toLocaleString(
          "en-IN"
        )} – ₹${estimate.price_range_high_inr.toLocaleString("en-IN")}`
      );
    }
    window.open(whatsappLink(parts.join("\n")), "_blank");
  };

  return (
    <div data-testid="designer-page">
      {/* Header */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-12">
        <div className="overline">The Custom Studio</div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl lg:text-7xl font-light text-[#1B3B36] leading-[1.02] tracking-tight max-w-4xl">
          Design a piece that could only be <em className="italic">yours</em>.
        </h1>
        <p className="mt-8 text-[16px] leading-relaxed text-[#4A4A4A] font-light max-w-2xl">
          Choose the object, describe the feeling, upload a reference if you
          like. Our studio will translate it into a woven-ready artwork and
          give you a transparent, hand-crafted estimate.
        </p>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
        {/* LEFT — Canvas & Preview */}
        <div className="lg:col-span-7">
          <div className="border border-[#D8D4CC] bg-[#EFECE5]/40 relative aspect-[4/3] dot-grid overflow-hidden">
            {generated ? (
              <img
                src={`data:${generated.mime_type};base64,${generated.image_base64}`}
                alt="AI generated design"
                data-testid="designer-generated-image"
                className="w-full h-full object-cover"
              />
            ) : refImage ? (
              <img
                src={`data:image/*;base64,${refImage}`}
                alt="Uploaded reference"
                className="w-full h-full object-contain p-6"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-center px-6">
                <div className="w-16 h-16 border border-[#1B3B36] rounded-full flex items-center justify-center text-[#1B3B36]">
                  <Sparkles size={22} />
                </div>
                <div className="font-serif text-2xl md:text-3xl text-[#1B3B36] mt-6">
                  Your design will appear here.
                </div>
                <p className="text-[14px] text-[#4A4A4A] mt-3 max-w-md font-light">
                  Describe a motif, feeling or memory on the right. Optionally
                  upload a reference image and let us imagine it woven.
                </p>
              </div>
            )}
            {loadingGen && (
              <div className="absolute inset-0 bg-[#F9F8F6]/70 backdrop-blur flex items-center justify-center">
                <div className="flex items-center gap-3 text-[#1B3B36]">
                  <Loader2 className="animate-spin" size={20} />
                  <span className="text-[13px] uppercase tracking-[0.22em]">
                    Weaving your vision…
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-3 items-center">
            <button
              data-testid="designer-upload-btn"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 border border-[#1B3B36] text-[#1B3B36] px-5 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-[#1B3B36] hover:text-[#F9F8F6] transition-colors"
            >
              <Upload size={14} /> Upload reference
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onFile}
              data-testid="designer-file-input"
            />
            {refImageName && (
              <span className="text-[12px] text-[#4A4A4A]">
                {refImageName} attached
              </span>
            )}
            {generated && (
              <a
                data-testid="designer-download-btn"
                href={`data:${generated.mime_type};base64,${generated.image_base64}`}
                download="iryaa-custom-design.png"
                className="inline-flex items-center gap-2 border border-[#1B3B36] text-[#1B3B36] px-5 py-3 text-[12px] uppercase tracking-[0.18em] hover:bg-[#1B3B36] hover:text-[#F9F8F6] transition-colors"
              >
                <Download size={14} /> Download
              </a>
            )}
          </div>

          <button
            data-testid="designer-generate-btn"
            onClick={runGenerate}
            disabled={loadingGen}
            className="mt-4 w-full inline-flex items-center justify-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26] transition-colors disabled:opacity-70"
          >
            {loadingGen ? (
              <>
                <Loader2 className="animate-spin" size={16} /> Generating…
              </>
            ) : (
              <>
                <Sparkles size={16} /> Generate AI Design
              </>
            )}
          </button>
        </div>

        {/* RIGHT — Control panel */}
        <div className="lg:col-span-5">
          <div className="border border-[#D8D4CC] p-6 md:p-8 bg-[#F9F8F6]">
            <div className="overline">01 · Object</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c.slug}
                  data-testid={`designer-cat-${c.slug}`}
                  onClick={() => {
                    setCategory(c.slug);
                    setMaterial(MATERIALS_BY_CAT[c.slug][0]);
                  }}
                  className={`px-4 py-3 text-[12px] uppercase tracking-[0.18em] border transition-colors ${
                    category === c.slug
                      ? "bg-[#1B3B36] text-[#F9F8F6] border-[#1B3B36]"
                      : "text-[#1B3B36] border-[#D8D4CC] hover:border-[#1B3B36]"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>

            <div className="overline mt-8">02 · Material</div>
            <select
              data-testid="designer-material"
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              className="mt-3 w-full border border-[#D8D4CC] bg-transparent px-4 py-3 text-[14px] focus:border-[#1B3B36] outline-none"
            >
              {currentMaterials.map((m) => (
                <option key={m} value={m}>
                  {m.replace("_", " ")}
                </option>
              ))}
            </select>

            <div className="overline mt-8">03 · Dimensions (cm)</div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <label className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">
                Width
                <input
                  type="number"
                  data-testid="designer-width"
                  value={width}
                  min={20}
                  onChange={(e) => setWidth(e.target.value)}
                  className="mt-1 w-full border border-[#D8D4CC] bg-transparent px-3 py-3 text-[14px] focus:border-[#1B3B36] outline-none"
                />
              </label>
              <label className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">
                Length
                <input
                  type="number"
                  data-testid="designer-length"
                  value={length}
                  min={20}
                  onChange={(e) => setLength(e.target.value)}
                  className="mt-1 w-full border border-[#D8D4CC] bg-transparent px-3 py-3 text-[14px] focus:border-[#1B3B36] outline-none"
                />
              </label>
              <label className="text-[11px] uppercase tracking-wider text-[#4A4A4A]">
                Qty
                <input
                  type="number"
                  data-testid="designer-quantity"
                  value={quantity}
                  min={1}
                  onChange={(e) => setQuantity(e.target.value)}
                  className="mt-1 w-full border border-[#D8D4CC] bg-transparent px-3 py-3 text-[14px] focus:border-[#1B3B36] outline-none"
                />
              </label>
            </div>

            <div className="overline mt-8">04 · Motif & feeling</div>
            <textarea
              data-testid="designer-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder="Describe the mood, colours, memory or motif…"
              className="mt-3 w-full border border-[#D8D4CC] bg-transparent px-4 py-3 text-[14px] focus:border-[#1B3B36] outline-none resize-none"
            />
            <div className="mt-4 grid grid-cols-2 gap-3">
              <select
                data-testid="designer-style"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                className="border border-[#D8D4CC] bg-transparent px-4 py-3 text-[13px] focus:border-[#1B3B36] outline-none"
              >
                {STYLES.map((s) => (
                  <option key={s} value={s}>
                    Style · {s}
                  </option>
                ))}
              </select>
              <select
                data-testid="designer-palette"
                value={palette}
                onChange={(e) => setPalette(e.target.value)}
                className="border border-[#D8D4CC] bg-transparent px-4 py-3 text-[13px] focus:border-[#1B3B36] outline-none"
              >
                {PALETTES.map((p) => (
                  <option key={p} value={p}>
                    Palette · {p}
                  </option>
                ))}
              </select>
            </div>

            <label className="mt-6 flex items-center gap-3 text-[13px] text-[#4A4A4A] cursor-pointer">
              <input
                data-testid="designer-custom-toggle"
                type="checkbox"
                checked={customDesign}
                onChange={(e) => setCustomDesign(e.target.checked)}
                className="w-4 h-4 accent-[#1B3B36]"
              />
              Include bespoke design fee (₹6,500) in estimate
            </label>

            <button
              data-testid="designer-estimate-btn"
              onClick={runEstimate}
              disabled={loadingEstimate}
              className="mt-6 w-full inline-flex items-center justify-center gap-3 border border-[#1B3B36] text-[#1B3B36] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#1B3B36] hover:text-[#F9F8F6] transition-colors disabled:opacity-70"
            >
              {loadingEstimate ? (
                <>
                  <Loader2 className="animate-spin" size={16} /> Calculating…
                </>
              ) : (
                <>Get Price Estimate</>
              )}
            </button>

            {estimate && (
              <div
                data-testid="designer-estimate-result"
                className="mt-6 border-t border-[#D8D4CC] pt-6"
              >
                <div className="overline text-[#84968C]">Indicative estimate</div>
                <div className="mt-2 font-serif text-4xl md:text-5xl text-[#1B3B36] leading-none">
                  ₹{estimate.price_range_low_inr.toLocaleString("en-IN")}
                  <span className="text-2xl text-[#84968C] mx-2">—</span>₹
                  {estimate.price_range_high_inr.toLocaleString("en-IN")}
                </div>
                <div className="mt-3 text-[12px] text-[#4A4A4A] leading-relaxed">
                  {estimate.breakdown_note}. Area {estimate.area_sqm} sqm ·
                  variance reflects handcrafted nature.
                </div>
                <button
                  data-testid="designer-whatsapp-enquire"
                  onClick={enquireOnWhatsApp}
                  className="mt-5 w-full inline-flex items-center justify-center gap-3 bg-[#1B3B36] text-[#F9F8F6] px-8 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-[#132A26] transition-colors"
                >
                  Enquire on WhatsApp <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
